import React, { useState, useRef } from 'react';
import {
    Brain,
    Settings,
    BookOpen,
    LayoutTemplate,
    RefreshCw,
    Check,
    Type,
    Copy,
    ArrowRight,
    Image as ImageIcon,
    FileText,
    X,
    UploadCloud,
    AlertCircle,
    Download
} from 'lucide-react';
import { fetchWithGeminiResilience } from './geminiClient';
import { jsPDF } from "jspdf";

export default function QuestionAdapter({ apiKey }) {
    const [originalQuestion, setOriginalQuestion] = useState("");
    const [selectedImage, setSelectedImage] = useState(null);
    const [base64Image, setBase64Image] = useState(null);
    const [imageMimeType, setImageMimeType] = useState(null);

    const [adaptedQuestion, setAdaptedQuestion] = useState("");
    const [subject, setSubject] = useState("ciencias");
    const [profile, setProfile] = useState("tipo2");
    const [format, setFormat] = useState("multipla_escolha");

    const [isLoading, setIsLoading] = useState(false);
    const [showPrompt, setShowPrompt] = useState(false);
    const [copied, setCopied] = useState(false);
    const [error, setError] = useState(null);

    const fileInputRef = useRef(null);

    // Fallback se a prop não vier (mas ideal é vir do Planner.js)
    // Mantendo a do arquivo original como fallback temporário ou use a do sistema
    const API_KEY_FIXA = apiKey || "AQ.Ab8RN6L9xqG2T6VdoL7ChC1S_aV8DS1a1UWdXhScIx9CHlwsHQ";

    const constructSystemPrompt = () => {
        return `
Atue como um Especialista em Educação Inclusiva e Neurociência da Aprendizagem.
Sua tarefa é adaptar questões escolares para alunos com necessidades específicas.

DADOS DE ENTRADA:
- Disciplina: ${subject.toUpperCase()}
- Perfil do Aluno: ${profile === 'tipo2' ? 'TIPO 2 (TDAH/Dislexia)' : 'TIPO 3 (Autismo/Cognitivo)'}
- Formato Desejado: ${format.toUpperCase()}

--- REGRAS GERAIS (PARA TODOS) ---
1. NUNCA use comandos negativos ("Não marque", "Exceto"). Sempre positivo.
2. NUNCA use "Todas as alternativas acima" ou "Nenhuma das anteriores".
3. Mantenha a língua original da questão.
4. Se houver imagem anexada, descreva-a na Parte I e use-a como base para a pergunta.
5. GABARITO NO FINAL: Agrupe TODOS os gabaritos comentados APENAS NO FINAL DO DOCUMENTO, após escrever TODAS as questões adaptadas. Nunca coloque o gabarito logo abaixo de cada questão individualmente.
6. MATEMÁTICA E SÍMBOLOS: 
   - PROIBIDO USAR LATEX ou códigos como $, \\frac, \\sqrt, \\cdot.
   - Use formatação simples e legível: escreva "raiz quadrada de" em vez do símbolo se for complexo, use "/" para divisão, "x" para multiplicação.
   - O foco é a acessibilidade: evite notações que pareçam "código de programação".
7. ARQUIVOS LONGOS E MÚLTIPLAS QUESTÕES (MUITO IMPORTANTE): 
   - Se receber várias questões (PDF/Docs/Texto), você DEVE adaptar ABSOLUTAMENTE TODAS AS QUESTÕES contidas no arquivo.
   - NÃO PARE na primeira questão. Adapte a Questão 1, depois a Questão 2, e assim sucessivamente até acabar o documento.
   - Mantenha a numeração original de cada questão para que o usuário não perca nada.

--- REGRAS ESPECÍFICAS POR PERFIL ---

${profile === 'tipo2' ? `
[PERFIL TIPO 2 - Foco: Organização e Atenção]
1. ESTRUTURA OBRIGATÓRIA:
   - PARTE I (DADOS/TEXTO): Coloque o texto base, dados numéricos e descrição da imagem aqui. Quebre em tópicos.
   - PARTE II (COMANDO): A pergunta em si. Se tiver 2 verbos, divida em itens a) e b).
2. TEXTO: Frases curtas, diretas. Sem metáforas.
3. DISTRATORES: Plausíveis, mas claramente incorretos.
` : `
[PERFIL TIPO 3 - Foco: Concretude e Compreensão]
1. LINGUAGEM: Use "Vamos entender...", "Imagine que...".
2. CONCRETUDE: Troque termos abstratos por exemplos do dia a dia (ex: "móvel" vira "carro").
3. DESTAQUE: Use **negrito** nas palavras-chave importantes (mas sem exageros).
4. AJUDA VISUAL: Se o formato for relacionar colunas, use termos como "Grupo A" e "Grupo B" ou "Ligue os pares".
`}

--- REGRAS DE FORMATO ---
${format === 'relacionar_colunas' ? "Crie duas colunas claras para associação. Use ( ) para preencher." : ""}
${format === 'lacunas' ? "Crie um quadro de palavras acima das frases para o aluno consultar." : ""}
${format === 'discursiva' ? "A resposta esperada deve ser curta e direta." : ""}
${format === 'multipla_escolha' ? "Crie 4 alternativas (A, B, C, D). Apenas uma correta." : ""}

GERAR SAÍDA AGORA. Lembre-se: se houver mais de uma questão na entrada, processe e retorne a adaptação de TODAS elas.
`;
    };

    const handleFileUpload = async (e) => {
        const file = e.target.files[0];
        if (file) {
            if (file.name.endsWith('.docx') || file.type === "application/vnd.openxmlformats-officedocument.wordprocessingml.document") {
                alert("A leitura direta de arquivos DOCX não está disponível nesta versão. Por favor, transforme seu arquivo em PDF, ou copie e cole o texto na área indicada.");
                if (fileInputRef.current) fileInputRef.current.value = "";
                return;
            }

            setImageMimeType(file.type);

            if (file.type === "application/pdf") {
                const reader = new FileReader();
                reader.onloadend = () => {
                    const base64String = reader.result.split(',')[1];
                    setBase64Image(base64String);
                    setSelectedImage(file.name); 
                };
                reader.readAsDataURL(file);
            } else if (file.type.startsWith("image/")) {
                const imageUrl = URL.createObjectURL(file);
                setSelectedImage(imageUrl);
                
                const reader = new FileReader();
                reader.onloadend = () => {
                    const base64String = reader.result.split(',')[1];
                    setBase64Image(base64String);
                };
                reader.readAsDataURL(file);
            } else {
                alert("Formato não suportado. Envie Imagem, PDF ou DOCX.");
            }
        }
    };

    const clearImage = () => {
        setSelectedImage(null);
        setBase64Image(null);
        setImageMimeType(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    };

    const callGemini = async () => {
        if (!API_KEY_FIXA || API_KEY_FIXA.length < 10) {
            alert("ERRO: Chave de API não configurada corretamente.");
            return;
        }

        if (!originalQuestion.trim() && !base64Image) return;

        setIsLoading(true);
        setError(null);
        setAdaptedQuestion("");

        try {
            const systemPrompt = constructSystemPrompt();
            const userParts = [];

            if (originalQuestion) {
                userParts.push({ text: originalQuestion });
            }
            if (base64Image) {
                userParts.push({
                    inlineData: {
                        mimeType: imageMimeType || "image/jpeg",
                        data: base64Image
                    }
                });
            }

            const payload = {
                contents: [{ parts: userParts }],
                systemInstruction: { parts: [{ text: systemPrompt }] }
            };

            const response = await fetchWithGeminiResilience(API_KEY_FIXA, payload);

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({ error: { message: response.statusText } }));
                throw new Error(errorData.error?.message || `Erro ${response.status}: ${response.statusText}`);
            }

            const data = await response.json();
            const generatedText = data.candidates?.[0]?.content?.parts?.[0]?.text;

            if (generatedText) {
                setAdaptedQuestion(generatedText);
            } else {
                throw new Error("A IA não retornou texto válido.");
            }

        } catch (err) {
            console.error(err);
            setError("Falha ao conectar com a inteligência. Verifique a conexão ou a API Key.");
        } finally {
            setIsLoading(false);
        }
    };

    const handleCopy = () => {
        if (!adaptedQuestion) return;
        const cleanText = adaptedQuestion.replace(/\*\*/g, '');

        const doCopy = (text) => {
            if (navigator.clipboard && navigator.clipboard.writeText) {
                navigator.clipboard.writeText(text).then(() => {
                    setCopied(true);
                    setTimeout(() => setCopied(false), 2000);
                });
            } else {
                const textArea = document.createElement("textarea");
                textArea.value = text;
                document.body.appendChild(textArea);
                textArea.select();
                document.execCommand('copy');
                document.body.removeChild(textArea);
                setCopied(true);
                setTimeout(() => setCopied(false), 2000);
            }
        };
        doCopy(cleanText);
    };

    const handleDownloadPDF = () => {
        if (!adaptedQuestion) return;
        
        const doc = new jsPDF();
        
        // Título
        doc.setFontSize(16);
        doc.setFont("helvetica", "bold");
        doc.text("Questões Adaptadas", 20, 20);
        
        doc.setFontSize(12);
        doc.setFont("helvetica", "normal");
        
        // Limpa a formatação markdown simples
        const cleanText = adaptedQuestion.replace(/\*\*/g, '').replace(/#/g, '');
        
        // Quebra o texto em linhas para caber na largura da página
        const splitText = doc.splitTextToSize(cleanText, 170);
        
        let cursorY = 30;
        
        for (let i = 0; i < splitText.length; i++) {
            if (cursorY > 280) {
                doc.addPage();
                cursorY = 20;
            }
            doc.text(splitText[i], 20, cursorY);
            cursorY += 7;
        }
        
        doc.save("questoes_adaptadas.pdf");
    };

    const renderFormattedText = (text) => {
        const parts = text.split(/(\*\*.*?\*\*)/g);
        return parts.map((part, index) => {
            if (part.startsWith('**') && part.endsWith('**')) {
                return <strong key={index} className="font-bold text-slate-900">{part.replace(/\*/g, '')}</strong>;
            }
            return part;
        });
    };

    return (
        <div className="h-full bg-slate-50 text-slate-800 font-sans selection:bg-orange-100 flex flex-col overflow-y-auto">

            {/* --- HEADER --- */}
            <header className="bg-white border-b border-slate-200 sticky top-0 z-10 shadow-sm shrink-0">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <div className="bg-orange-600 p-2 rounded-lg text-white">
                            <Brain size={24} />
                        </div>
                        <h1 className="text-xl font-bold text-slate-800 tracking-tight">
                            Adaptador<span className="text-orange-600">Inclusivo</span>
                        </h1>
                    </div>

                    <div className="flex items-center gap-4">
                        <button
                            onClick={() => setShowPrompt(!showPrompt)}
                            className="text-slate-500 hover:text-orange-600 text-sm font-medium flex items-center gap-1 transition-colors"
                        >
                            <Settings size={16} />
                            {showPrompt ? 'Ocultar Prompt' : 'Ver Prompt Lógico'}
                        </button>

                        <div className="h-6 w-px bg-slate-200"></div>
                        <span className="text-sm text-orange-600 font-bold bg-orange-50 px-2 py-1 rounded border border-orange-100">IA ATIVA ⚡</span>
                    </div>
                </div>
            </header>

            {/* --- DEBUG PROMPT --- */}
            {showPrompt && (
                <div className="bg-slate-900 text-slate-300 p-6 text-sm font-mono border-b border-slate-700 shadow-inner animate-in slide-in-from-top-4 shrink-0">
                    <div className="max-w-7xl mx-auto">
                        <h3 className="text-white font-bold mb-2 flex items-center gap-2">
                            <Settings size={16} /> Prompt Real Enviado para o Gemini
                        </h3>
                        <p className="mb-4 text-slate-400">Este é o comando que está sendo enviado para a API neste momento:</p>
                        <div className="bg-black/50 p-4 rounded border border-slate-700 whitespace-pre-wrap max-h-60 overflow-y-auto custom-scrollbar">
                            {constructSystemPrompt()}
                            <div className="mt-4 pt-4 border-t border-slate-700 text-green-400">
                                + Conteúdo do Usuário: {originalQuestion ? `"${originalQuestion.substring(0, 50)}..."` : "(Vazio)"}
                                <br />
                                + Imagem: {base64Image ? "Sim (Base64 anexado)" : "Não"}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 w-full flex-1 flex flex-col">

                {/* --- BARRA DE CONTROLE --- */}
                <div className="bg-white p-4 rounded-xl shadow-sm border border-slate-200 mb-6 flex flex-col lg:flex-row gap-4 items-end lg:items-center justify-between shrink-0">

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 w-full lg:w-auto flex-1">
                        {/* Disciplina */}
                        <div className="flex flex-col gap-1">
                            <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Disciplina</label>
                            <div className="relative">
                                <div className="absolute left-3 top-3 text-slate-400"><BookOpen size={18} /></div>
                                <select
                                    className="w-full pl-10 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-orange-500 focus:border-transparent outline-none transition-all appearance-none cursor-pointer text-slate-700 font-medium"
                                    value={subject}
                                    onChange={(e) => setSubject(e.target.value)}
                                >
                                    <option value="Língua Portuguesa">Língua Portuguesa</option>
                                    <option value="Matemática">Matemática</option>
                                    <option value="História">História (History)</option>
                                    <option value="Geografia">Geografia (Geography)</option>
                                    <option value="Language and Arts">Language and Arts</option>
                                    <option value="Ciências da Natureza">Ciências da Natureza (Science)</option>
                                    <option value="Biologia">Biologia</option>
                                    <option value="Química">Química</option>
                                    <option value="Física">Física</option>
                                    <option value="Filosofia">Filosofia</option>
                                    <option value="Sociologia">Sociologia</option>
                                    <option value="Literatura">Literatura</option>
                                    <option value="Arte">Arte</option>
                                    <option value="Physical Education">Physical Education</option>
                                    <option value="Public Speaking">Public Speaking</option>
                                    <option value="Drama">Drama</option>
                                    <option value="Creative Tech">Creative Tech</option>
                                    <option value="Music">Music</option>
                                </select>
                            </div>
                        </div>

                        {/* Perfil */}
                        <div className="flex flex-col gap-1">
                            <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Perfil do Aluno</label>
                            <div className="relative">
                                <div className="absolute left-3 top-3 text-slate-400"><Brain size={18} /></div>
                                <select
                                    className="w-full pl-10 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-orange-500 focus:border-transparent outline-none transition-all appearance-none cursor-pointer text-slate-700 font-medium"
                                    value={profile}
                                    onChange={(e) => setProfile(e.target.value)}
                                >
                                    <option value="tipo2">Tipo 2</option>
                                    <option value="tipo3">Tipo 3</option>
                                </select>
                            </div>
                        </div>

                        {/* Formato */}
                        <div className="flex flex-col gap-1">
                            <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Formato de Saída</label>
                            <div className="relative">
                                <div className="absolute left-3 top-3 text-slate-400"><LayoutTemplate size={18} /></div>
                                <select
                                    className="w-full pl-10 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-orange-500 focus:border-transparent outline-none transition-all appearance-none cursor-pointer text-slate-700 font-medium"
                                    value={format}
                                    onChange={(e) => setFormat(e.target.value)}
                                >
                                    <option value="multipla_escolha">Múltipla Escolha</option>
                                    <option value="discursiva">Discursiva</option>
                                    <option value="lacunas">Completar Lacunas</option>
                                    <option value="relacionar_colunas">Relacionar Colunas</option>
                                </select>
                            </div>
                        </div>
                    </div>

                    {/* Botão */}
                    <button
                        onClick={callGemini}
                        disabled={isLoading || (!originalQuestion.trim() && !base64Image)}
                        className={`
                        flex items-center justify-center gap-2 px-8 py-3 rounded-lg font-bold text-white shadow-lg shadow-orange-500/30 transition-all transform active:scale-95 w-full lg:w-auto
                        ${isLoading || (!originalQuestion.trim() && !base64Image) ? 'bg-slate-300 cursor-not-allowed shadow-none' : 'bg-orange-600 hover:bg-orange-700 hover:-translate-y-0.5'}
                    `}
                    >
                        {isLoading ? (
                            <React.Fragment>
                                <RefreshCw className="animate-spin" size={20} />
                                Processando...
                            </React.Fragment>
                        ) : (
                            <React.Fragment>
                                <Check size={20} />
                                Gerar Adaptação
                            </React.Fragment>
                        )}
                    </button>
                </div>

                {/* --- ÁREA DE TRABALHO --- */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 min-h-[500px] flex-1">

                    {/* Input Area */}
                    <div className="flex flex-col h-full bg-slate-100/50 p-1 rounded-2xl">
                        <div className="flex items-center justify-between mb-2 px-1">
                            <label className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                                <Type size={16} className="text-orange-500" />
                                Entrada (Texto ou Imagem)
                            </label>
                        </div>

                        <div className="flex-1 flex flex-col rounded-xl border border-slate-300 bg-white overflow-hidden focus-within:ring-2 focus-within:ring-orange-500 focus-within:border-transparent shadow-sm transition-all h-full">

                            {/* Área de Arquivo (Se houver) */}
                            {selectedImage && (
                                <div className="relative bg-slate-100 border-b border-slate-200 p-4 flex justify-center items-center h-48 shrink-0 animate-in fade-in zoom-in duration-300">
                                    {imageMimeType === 'application/pdf' ? (
                                        <div className="flex flex-col items-center text-slate-500">
                                            <FileText size={48} className="mb-2 text-orange-500" />
                                            <span className="font-semibold text-center max-w-[200px] truncate">{selectedImage}</span>
                                            <span className="text-xs">PDF Anexado</span>
                                        </div>
                                    ) : (
                                        <img
                                            src={selectedImage}
                                            alt="Preview da Questão"
                                            className="h-full w-auto object-contain rounded shadow-sm"
                                        />
                                    )}
                                    <button
                                        onClick={clearImage}
                                        className="absolute top-2 right-2 bg-slate-800/70 text-white p-1.5 rounded-full hover:bg-red-500 transition-colors"
                                        title="Remover arquivo"
                                    >
                                        <X size={16} />
                                    </button>
                                    <div className="absolute bottom-2 left-2 bg-orange-600 text-white text-[10px] px-2 py-0.5 rounded-full font-bold flex items-center gap-1">
                                        {imageMimeType === 'application/pdf' ? <FileText size={10} /> : <ImageIcon size={10} />} PRONTO
                                    </div>
                                </div>
                            )}

                            <textarea
                                className="flex-1 w-full p-4 outline-none resize-none text-slate-600 leading-relaxed text-base placeholder:text-slate-300 min-h-[200px]"
                                placeholder={selectedImage ? "Adicione alguma instrução extra se quiser..." : "Cole o texto da questão aqui OU carregue um arquivo (Img/PDF/Docx) usando o botão abaixo..."}
                                value={originalQuestion}
                                onChange={(e) => setOriginalQuestion(e.target.value)}
                            />

                            {/* Botão de Upload dentro da área de input */}
                            <div className="p-3 bg-slate-50 border-t border-slate-100 flex justify-between items-center shrink-0">
                                <input
                                    type="file"
                                    ref={fileInputRef}
                                    onChange={handleFileUpload}
                                    accept="image/*,application/pdf,.docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                                    className="hidden"
                                />
                                <button
                                    onClick={() => fileInputRef.current.click()}
                                    className="flex items-center gap-2 text-sm font-medium text-orange-600 hover:text-orange-800 hover:bg-orange-50 px-3 py-2 rounded-lg transition-colors"
                                >
                                    <UploadCloud size={18} />
                                    {selectedImage ? 'Trocar Arquivo' : 'Carregar Img/PDF/Docx'}
                                </button>
                                <span className="text-xs text-slate-400 hidden sm:inline">Suporta JPG, PNG, PDF, DOCX</span>
                            </div>
                        </div>
                    </div>

                    {/* Output Area */}
                    <div className="flex flex-col h-full bg-slate-100/50 p-1 rounded-2xl">
                        <div className="flex items-center justify-between mb-2 px-1">
                            <label className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                                <Brain size={16} className="text-green-500" />
                                Questão Adaptada (IA Real)
                            </label>

                            {adaptedQuestion && (
                                <div className="flex gap-2">
                                    <button
                                        onClick={handleDownloadPDF}
                                        className="text-xs font-medium text-orange-600 hover:text-orange-800 flex items-center gap-1 px-2 py-1 rounded hover:bg-orange-50 transition-colors border border-orange-200"
                                    >
                                        <Download size={14} />
                                        Baixar PDF
                                    </button>
                                    <button
                                        onClick={handleCopy}
                                        className="text-xs font-medium text-orange-600 hover:text-orange-800 flex items-center gap-1 px-2 py-1 rounded hover:bg-orange-50 transition-colors border border-orange-200"
                                    >
                                        {copied ? <Check size={14} /> : <Copy size={14} />}
                                        {copied ? 'Copiado!' : 'Copiar'}
                                    </button>
                                </div>
                            )}
                        </div>

                        <div className="flex-1 relative bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden h-full">
                            {isLoading ? (
                                <div className="absolute inset-0 p-6 flex flex-col items-center justify-center text-center animate-pulse">
                                    <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mb-4">
                                        <RefreshCw className="animate-spin text-orange-600" size={32} />
                                    </div>
                                    <p className="text-orange-800 font-medium">Conectando ao Gemini...</p>
                                    <p className="text-sm text-slate-500 mt-2">Lendo seu texto e adaptando pedagogicamente.</p>
                                </div>
                            ) : error ? (
                                <div className="absolute inset-0 flex flex-col items-center justify-center text-red-500 p-8 text-center">
                                    <AlertCircle size={48} className="mb-4 opacity-50" />
                                    <p className="font-bold">Ocorreu um erro</p>
                                    <p className="text-sm mt-2">{error}</p>
                                    <p className="text-xs mt-2 text-slate-500">Verifique sua chave de autenticação.</p>
                                    <button onClick={callGemini} className="mt-4 text-sm underline hover:text-red-700">Tentar Novamente</button>
                                </div>
                            ) : adaptedQuestion ? (
                                <div className="h-full overflow-y-auto p-6 custom-scrollbar">
                                    <div className="prose prose-slate prose-sm max-w-none">
                                        {adaptedQuestion.split('\n').map((line, i) => {
                                            const cleanedLine = line.replace(/\$/g, '');

                                            const isHeader = cleanedLine.includes('PARTE I') || cleanedLine.includes('PARTE II') || cleanedLine.includes('VAMOS ENTENDER') || cleanedLine.startsWith('#');

                                            const isListItem = cleanedLine.trim().startsWith('- ') || cleanedLine.trim().startsWith('• ');

                                            if (isHeader) {
                                                return <h3 key={i} className="text-orange-700 font-bold text-lg mt-6 mb-2 border-b border-orange-100 pb-1">{cleanedLine.replace(/#/g, '').replace(/\*/g, '')}</h3>
                                            }

                                            return (
                                                <p key={i} className={`mb-2 text-slate-600 min-h-[1.5em] ${isListItem ? 'pl-4' : ''}`}>
                                                    {renderFormattedText(cleanedLine)}
                                                </p>
                                            );
                                        })}
                                    </div>
                                </div>
                            ) : (
                                <div className="absolute inset-0 flex flex-col items-center justify-center text-slate-400 p-8 text-center">
                                    <div className="bg-slate-100 p-4 rounded-full mb-4">
                                        <ArrowRight size={24} className="text-slate-300" />
                                    </div>
                                    <p className="font-medium">Sua adaptação aparecerá aqui.</p>
                                    <p className="text-sm mt-2 max-w-xs text-slate-400">
                                        Digite a questão ao lado e clique em "Gerar".
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>

                </div>
            </main>
        </div>
    );
}
